import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  searchInventory,
  submitInventoryAdjustment,
  fetchRecentAdjustments,
  clearAdjustmentMessages
} from '../../store/slices/inventorySlice';
import {
  Search,
  Plus,
  Minus,
  Save,
  CheckCircle,
  XCircle,
  Package,
  Clock,
  User,
  FileText,
  Loader2
} from 'lucide-react';

const InventoryAdjustment: React.FC = () => {
  const dispatch = useAppDispatch();
    // Redux state
  const { 
    searchResults, 
    searchLoading,
    adjustmentLoading,
    adjustmentError,
    adjustmentSuccess,
    recentAdjustments,
    adjustmentsLoading
  } = useAppSelector((state) => state.inventory);

  // Search and item state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  
  // Adjustment form state
  const [adjustmentForm, setAdjustmentForm] = useState({
    adjustment_type: 'increase' as 'increase' | 'decrease',
    quantity: 1,
    reason: '',
    notes: '',
    selected_location: '',
    selected_location_type: 'bin' as 'bin' | 'rack'
  });
  
  // UI state
  const [activeTab, setActiveTab] = useState<'adjust' | 'history'>('adjust');

  // Search for inventory items
  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    
    dispatch(searchInventory({
      style_code: searchTerm
    }));
  };

  // Select item for adjustment
  const handleItemSelect = (item: any) => {
    setSelectedItem(item);
    if (item.locations && item.locations.length > 0) {
      setAdjustmentForm(prev => ({
        ...prev,
        selected_location: item.locations[0].location,
        selected_location_type: item.locations[0].type
      }));
    }
  };

  // Handle adjustment form submission
  const handleSubmitAdjustment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem || !adjustmentForm.quantity || !adjustmentForm.reason) {
      return;
    }

    const adjustmentData = {
      sku: selectedItem.sku,
      adjustment_type: adjustmentForm.adjustment_type,
      quantity: adjustmentForm.quantity,
      reason: adjustmentForm.reason,
      notes: adjustmentForm.notes || undefined,
      location: adjustmentForm.selected_location,
      location_type: adjustmentForm.selected_location_type
    };

    try {
      await dispatch(submitInventoryAdjustment(adjustmentData)).unwrap();
      
      // Reset form on success
      setSelectedItem(null);
      setAdjustmentForm({
        adjustment_type: 'increase',
        quantity: 1,
        reason: '',
        notes: '',
        selected_location: '',
        selected_location_type: 'bin'
      });
      setSearchTerm('');
      
      // Refresh recent adjustments if on history tab
      if (activeTab === 'history') {
        dispatch(fetchRecentAdjustments({ limit: 50 }));
      }
    } catch (error) {
      // Error is handled by Redux state
    }
  };

  // Load recent adjustments on component mount and tab change
  useEffect(() => {
    if (activeTab === 'history') {
      dispatch(fetchRecentAdjustments({ limit: 50 }));
    }
  }, [activeTab, dispatch]);

  // Clear messages on component mount
  useEffect(() => {
    dispatch(clearAdjustmentMessages());
  }, [dispatch]);

  // Auto-hide success message
  useEffect(() => {
    if (adjustmentSuccess) {
      const timer = setTimeout(() => {
        dispatch(clearAdjustmentMessages());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [adjustmentSuccess, dispatch]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Inventory Adjustments</h2>
        <p className="text-gray-600">Adjust inventory quantities for discrepancies and corrections</p>
      </div>      {/* Success/Error Messages */}
      {adjustmentSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
            <p className="text-sm text-green-800">Inventory adjustment processed successfully!</p>
          </div>
        </div>
      )}

      {adjustmentError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XCircle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-sm text-red-800">{adjustmentError}</p>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('adjust')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'adjust'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Package className="h-4 w-4 inline mr-2" />
            Make Adjustment
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Clock className="h-4 w-4 inline mr-2" />
            Adjustment History
          </button>
        </nav>
      </div>

      {activeTab === 'adjust' ? (
        <div className="space-y-6">
          {/* Search Section */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Search Inventory Item</h3>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter style code, SKU, or partial name..."
                />
              </div>              <button
                onClick={handleSearch}
                disabled={!searchTerm.trim() || searchLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="border border-gray-200 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 p-4 border-b border-gray-200">Search Results</h3>
              <div className="divide-y divide-gray-200">
                {searchResults.map((item) => (
                  <div
                    key={item.sku}
                    onClick={() => handleItemSelect(item)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${
                      selectedItem?.sku === item.sku ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-gray-900">{item.sku}</div>
                        <div className="text-sm text-gray-600">{item.style_code} - {item.color} - {item.size}</div>
                        <div className="text-sm text-gray-500">Current Stock: {item.quantity} units</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Locations: {item.locations.map(loc => `${loc.location} (${loc.quantity})`).join(', ')}
                        </div>
                      </div>
                      {selectedItem?.sku === item.sku && (
                        <CheckCircle className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Adjustment Form */}
          {selectedItem && (
            <form onSubmit={handleSubmitAdjustment} className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Adjustment Details</h3>
              
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                <h4 className="font-medium text-blue-900">Selected Item</h4>
                <p className="text-sm text-blue-800">
                  {selectedItem.sku} - Current Stock: {selectedItem.quantity} units
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adjustment Type</label>
                  <div className="space-y-2">                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="increase"
                        checked={adjustmentForm.adjustment_type === 'increase'}
                        onChange={() => setAdjustmentForm(prev => ({ ...prev, adjustment_type: 'increase' }))}
                        className="mr-2"
                      />
                      <Plus className="h-4 w-4 text-green-500 mr-1" />
                      Increase Quantity
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="decrease"
                        checked={adjustmentForm.adjustment_type === 'decrease'}
                        onChange={() => setAdjustmentForm(prev => ({ ...prev, adjustment_type: 'decrease' }))}
                        className="mr-2"
                      />
                      <Minus className="h-4 w-4 text-red-500 mr-1" />
                      Decrease Quantity
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adjustment Quantity *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={adjustmentForm.quantity}
                    onChange={(e) => setAdjustmentForm(prev => ({ 
                      ...prev, 
                      quantity: parseInt(e.target.value) || 1 
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>                  <select
                    value={adjustmentForm.selected_location}
                    onChange={(e) => {
                      const selectedLoc = selectedItem.locations.find((loc: any) => loc.location === e.target.value);
                      setAdjustmentForm(prev => ({
                        ...prev,
                        selected_location: e.target.value,
                        selected_location_type: selectedLoc?.type || 'bin'
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {selectedItem.locations.map((location: any) => (
                      <option key={location.location} value={location.location}>
                        {location.location} ({location.type}) - {location.quantity} units
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason *
                  </label>
                  <select
                    value={adjustmentForm.reason}
                    onChange={(e) => setAdjustmentForm(prev => ({ ...prev, reason: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select reason...</option>
                    <option value="Physical count discrepancy">Physical count discrepancy</option>
                    <option value="Damaged goods">Damaged goods</option>
                    <option value="Lost items">Lost items</option>
                    <option value="Found items">Found items</option>
                    <option value="Return to vendor">Return to vendor</option>
                    <option value="System error correction">System error correction</option>
                    <option value="Transfer correction">Transfer correction</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  value={adjustmentForm.notes}
                  onChange={(e) => setAdjustmentForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional additional details about the adjustment..."
                />
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedItem(null);
                    setAdjustmentForm({
                      adjustment_type: 'increase',
                      quantity: 1,
                      reason: '',
                      notes: '',
                      selected_location: '',
                      selected_location_type: 'bin'
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>                <button
                  type="submit"
                  disabled={adjustmentLoading || !adjustmentForm.reason || !adjustmentForm.quantity}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {adjustmentLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Submit Adjustment
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      ) : (
        /* Adjustment History Tab */
        <div className="space-y-4">          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Recent Adjustments</h3>
            <button
              onClick={() => dispatch(fetchRecentAdjustments({ limit: 50 }))}
              disabled={adjustmentsLoading}
              className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
            >
              {adjustmentsLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {adjustmentsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading adjustment history...</p>
            </div>
          ) : recentAdjustments.length > 0 ? (
            <div className="space-y-3">
              {recentAdjustments.map((adjustment) => (
                <div key={adjustment.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-3">
                        <span className="font-medium text-gray-900">{adjustment.sku}</span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          adjustment.adjustment_type === 'increase'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {adjustment.adjustment_type === 'increase' ? (
                            <Plus className="h-3 w-3 mr-1" />
                          ) : (
                            <Minus className="h-3 w-3 mr-1" />
                          )}
                          {adjustment.adjustment_type}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <div>Quantity: {adjustment.old_quantity} → {adjustment.new_quantity} 
                          ({adjustment.adjustment_type === 'increase' ? '+' : '-'}{adjustment.adjustment_quantity})
                        </div>
                        <div>Location: {adjustment.location} ({adjustment.location_type})</div>
                        <div>Reason: {adjustment.reason}</div>
                        {adjustment.notes && <div>Notes: {adjustment.notes}</div>}
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <User className="h-3 w-3 mr-1" />
                        {adjustment.adjusted_by}
                        <Clock className="h-3 w-3 ml-3 mr-1" />
                        {new Date(adjustment.adjusted_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No recent adjustments found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InventoryAdjustment;
